<template>
  <b-card-actions
    ref="cardAction"
    :class="'col-md-' + box.boxWidth + ' mycard'"
    :title="boxTitle"
    :sub-title="box.projectName"
    :weighted="box.boxConfig ? box.boxConfig.weightColumn : null"
    :unweighted-base="box.boxConfig ? box.boxConfig.showUnweightedBase : false"
    :search="searchLabel"
    :in-progress="inProgress"
    :box-height="box.boxHeight"
    :searchable="isSearchable"
    :currently-in-my-dashboard="currentlyInMyDashboard"
    :is-draft="(box.draftBy > 0 ? true : false)"
    :drills="drills.length"
    :drills-title="drillsTitle"
    :current-box-index="currentBoxIndex"
    :box-description="box.description"
    :box-message="result && result.systemMessage ? result.systemMessage : null"
    :show-pagination="box.boxConfig && (box.boxConfig.type === 'data' || box.boxConfig.type === 'external') && items.length > 0"
    :page-info="currentPage"
    :total-rows="items.length"
    :per-page="perPage"
    @edit="$emit('edit', box)"
    @delete="$emit('delete', box)"
    @download="$emit('download', box)"
    @search="$emit('search', box)"
    @clone="$emit('clone', box, drillFilters)"
    @clone_to_my_dashboard="$emit('clone_to_my_dashboard', box)"
    @drill="$emit('drill')"
    @publish="$emit('publish', box)"
    @clear="clearSearch"
    @drillBack="drillBack"
    @pageChanged="pageChanged"
  >

    <h5
      v-if="!inProgress && !authorized"
      class="text-center"
    >
      Not authorized!
    </h5>

    <template v-if="authorized">
      <treemap
        v-if="items && items.length && !inProgress && box.boxConfig && box.boxConfig.display === 'treemap'"
        :id="'boxExport_' + box.id"
        :series-data="series"
        :box-height="box.boxHeight"
        :color-steps="box.boxConfig.colorSteps"
        :show-base="compute_showbase"
        :percentage="compute_percentage"
      />
      <highcharts
        v-else-if="items && items.length && !inProgress && box.boxConfig && box.boxConfig.display !== 'table' && (box.boxConfig.type === 'score' || box.boxConfig.type === 'standard' || box.boxConfig.type === 'frequency' || box.boxConfig.type === 'custom')"
        :id="'boxExport_' + box.id"
        :options="chartOptions"
      />

      <b-table
        v-if="!inProgress && items.length > 0 && box.boxConfig && (box.boxConfig.display === 'table' || box.boxConfig.type === 'external') && (box.boxConfig.type === 'score' || box.boxConfig.type === 'standard' || box.boxConfig.type === 'frequency' || box.boxConfig.type === 'data' || box.boxConfig.type === 'external' || box.boxConfig.type === 'custom')"
        :id="'boxExport_' + box.id"
        style="height:100%"
        :sticky-header="stickyHeader"
        :no-border-collapse="noCollapse"
        :per-page="perPage"
        :current-page="currentPage"
        responsive
        :items="items"
        :fields="fields"
        :style="cssVars"
        :sort-compare="mysorter"
      >
        <!-- sort-compare  -->
        <template #head()="data">
          <!-- eslint-disable vue/no-v-html -->
          <span
            class="text-nowrap"
            v-html="$t(searchQuestion(data.field.key)) + (data.field.testLabel ? data.field.testLabel : '')"
          />
          <!--eslint-enable-->
        </template>
        <template #cell()="data">
          <!-- eslint-disable vue/no-v-html -->
          <sup v-if="data.value.zbase">%</sup>
          <span
            v-if="data.field.key !== 'orjref' && (!drills.length || (drills.length && currentBoxIndex === drills.length - 1))"
            class="text-nowrap"
            :class="{'above': data.value.target =='above', 'below': data.value.target =='below'}"
            :title="data.value.zbase ? 'n: ' + formatBase(data.value.zbase) : null"
            @click="cellClick(data)"
            v-html="$t(data.value.val == ' ' ? 'TOTAL' : data.value.val)"
          />
          <b-link
            v-if="data.field.key === 'orjref' || (drills.length && currentBoxIndex < drills.length - 1)"
            @click="cellClick(data)"
          >
            {{ $t(data.value.val) }}
          </b-link>

          <span v-html="data.value.lbl" />
          <!--eslint-enable-->
        </template>
      </b-table>

      <h1
        v-if="!inProgress && items.length === 0"
        class="text-center"
      >
        {{ $t('No data') }}
      </h1>

      <statistic-card-vertical
        v-if="items && items.length && box.boxConfig && box.boxConfig.type === 'infographic' && box.boxConfig.layout === 'vertical'"
        :id="'boxExport_' + box.id"
        hide-chart
        :color="box.boxConfig.statisticVariant"
        :icon="box.boxConfig.statisticIcon"
        :statistic="(box.boxConfig.columnOperator === 'Percent' ? '%' : '') + (items.length > 0 ? items[0].value.val : 0)"
        :statistic-text="box.boxConfig.statisticText"
        :statistic-title="box.boxConfig.showBase ? 'n: ' + (items.length > 0 ? formatBase(items[0].base.val) : 0) : ''"
      />

      <statistic-card-donut
        v-if="items && items.length && box.boxConfig && box.boxConfig.type === 'infographic' && box.boxConfig.layout === 'donut'"
        :id="'boxExport_' + box.id"
        hide-chart
        :color="box.boxConfig.statisticVariant"
        :icon="box.boxConfig.statisticIcon"
        :statistic="(box.boxConfig.columnOperator === 'Percent' ? '%' : '') + (items.length > 0 ? items[0].value.val : 0)"
        :statistic-value="(items.length > 0 ? items[0].value.val : 0)"
        :statistic-text="box.boxConfig.statisticText"
        :statistic-title="box.boxConfig.showBase ? 'n: ' + (items.length > 0 ? formatBase(items[0].base.val) : 0) : ''"
      />

      <statistic-card-horizontal
        v-if="items && items.length && box.boxConfig && box.boxConfig.type === 'infographic' && box.boxConfig.layout === 'horizontal'"
        :id="'boxExport_' + box.id"
        :color="box.boxConfig.statisticVariant"
        :icon="box.boxConfig.statisticIcon"
        :statistic="(box.boxConfig.columnOperator === 'Percent' ? '%' : '') + (items.length > 0 ? items[0].value.val : 0)"
        :statistic-text="box.boxConfig.statisticText"
        :statistic-title="box.boxConfig.showBase ? 'n: ' + (items.length > 0 ? formatBase(items[0].base.val) : 0) : ''"
      />

      <chart-map
        v-if="items && items.length && box.boxConfig && box.boxConfig.type === 'infographic' && box.boxConfig.layout === 'map-tr'"
        v-model="map_data"
        :color-steps="box.boxConfig.colorSteps"
        :compare-with-general="box.boxConfig.compareWithGeneral"
        :color-below="box.boxConfig.colorBelow"
        :color-above="box.boxConfig.colorAbove"
        :region-mapping="box.boxConfig.regionMapping"
        @mapClick="mapClick"
      />

      <card-statistics-group
        v-if="box.boxConfig && box.boxConfig.type === 'infographicGroup'"
        :id="'boxExport_' + box.id"
        :layout="box.boxConfig.layout"
        :statistics-items="ig_group"
        :box-height="box.boxHeight"
      />
    </template>

    <!-- appeal-->
    <modal-survey
      :ref="'surveyModal' + box.id"
      :boxid="box.id"
      :appeal="true"
    />
  </b-card-actions>
</template>

<script>
import BCardActions from '@core/components/b-card-actions/BCardActions.vue'
import {
  BTable, BLink,
} from 'bootstrap-vue'
import useJwt from '@/auth/jwt/useJwt'
import dayjs from 'dayjs'
import StatisticCardVertical from '@core/components/statistics-cards/StatisticCardVertical.vue'
import StatisticCardHorizontal from '@core/components/statistics-cards/StatisticCardHorizontal.vue'
import StatisticCardDonut from '@core/components/statistics-cards/StatisticCardDonut.vue'
import CardStatisticsGroup from '@/layouts/components/CardStatisticsGroup.vue'
import { getUserData } from '@/auth/utils'
import { numberFormat } from '@core/utils/utils'
import ChartMap from '@/layouts/components/ChartMap.vue'
import ModalSurvey from '@/layouts/components/ModalSurvey.vue'

import Highcharts from 'highcharts'
import { Chart } from 'highcharts-vue'

import HcMore from 'highcharts/highcharts-more'
import Treemap from '@/layouts/components/Treemap.vue'

HcMore(Highcharts)

export default {
  components: {
    BCardActions,
    BTable,
    BLink,
    StatisticCardVertical,
    StatisticCardHorizontal,
    StatisticCardDonut,
    CardStatisticsGroup,
    highcharts: Chart,
    ChartMap,
    ModalSurvey,
    Treemap,
  },
  mixins: [numberFormat],
  props: {
    xbox: {
      type: Object,
      default: null,
    },
    currentlyInMyDashboard: {
      type: Boolean,
      default: false,
    },
    runningForPdf: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      maxSeriesLimit: 15,
      searchQuestions: [],
      searchValue: null,
      drills: [],
      drillFilters: [],
      currentBoxIndex: 0,
      parentBoxConfig: null,
      perPage: 50,
      totalRows: 1,
      currentPage: 1,
      box: this.xbox,
      duration: null,
      result: null,
      stickyHeader: true,
      noCollapse: true,
      boxtime: null,
      inProgress: true,
      fields: [],
      items: [],
      status: [{
        1: 'Current', 2: 'Professional', 3: 'Rejected', 4: 'Resigned', 5: 'Applied',
      },
      {
        1: 'light-primary', 2: 'light-success', 3: 'light-danger', 4: 'light-warning', 5: 'light-info',
      }],
      series: [],
      categories: [],
      authorized: false,
      optionQuestion: [],
      map_data: null,
      oldBoxId: null,
      rowHolder: null,
      totalBaseForPie: null,
      compareMessage: null,
    }
  },
  computed: {
    compute_showbase() {
      return this.box.boxConfig.showBase && (this.box.boxConfig.type === 'frequency' && (this.box.boxConfig.percentage !== 'no'))
    },
    compute_percentage() {
      return this.box.boxConfig.percentage !== 'no' ? '%' : ''
    },
    boxTitle() {
      let title = ''
      if (this.box) {
        title = this.box.boxTitle
        if (this.result && this.box.boxConfig && this.box.boxConfig.type === 'data') {
          title += ` (n: ${this.result.recordCount})`
        }
      }
      if (this.compareMessage) {
        title = `${this.compareMessage} ${title}`
      }

      return title
    },
    drillsTitle() {
      let title = ''
      // drill titles
      const items = []
      if (this.drillFilters) {
        this.drillFilters.forEach(d => {
          items.push(d.map(dd => dd.value).join(' / '))
        })
        title = `${title} ${items.join(' / ')}`
      }

      return title
    },
    isSearchable() {
      if (this.box && this.box.boxConfig && ['score', 'data', 'frequency'].includes(this.box.boxConfig.type) && this.box.boxConfig.display === 'table' && (this.box.boxConfig.questionName || this.box.boxConfig.questionNames.length || this.box.boxConfig.infoQuestionNames.length)) {
        return true
      }
      return false
    },
    searchLabel() {
      if (this.searchValue) {
        return this.searchValue
      }
      return null
    },
    chartOptions() {
      return {
        chart: {
          height: this.box.boxHeight - (this.box.description || (this.result && this.result.systemMessage) || this.currentBoxIndex > 0 ? 80 : 0),
          // height: this.box.boxHeight,
          type: this.box.boxConfig.display,
          backgroundColor: this.$store.state.appConfig.layout.skin === 'dark' ? '#283046' : 'transparent',
          plotBackgroundColor: this.$store.state.appConfig.layout.skin === 'dark' ? '#283046' : 'transparent',
          plotBackgroundImage: null,
          plotBorderWidth: 0,
          plotShadow: false,
          style: { fontFamily: "'Montserrat', 'Helvetica Neue', 'Helvetica', 'Arial', 'sans-serif'", fontSize: '14px', fontWeight: 500 },
          margin: this.box.boxConfig.display === 'gauge' ? [10, 0, 50, 0] : null,
        },
        subtitle: {
          text: this.totalBaseForPie,
          verticalAlign: 'bottom',
          align: 'center',
        },
        credits: { enabled: false },
        title: { text: '' },
        legend: {
          enabled: true,
          reversed: !!(this.box.boxConfig.stacking && this.box.boxConfig.display === 'bar'),
          itemStyle: {
            color: this.$store.state.appConfig.layout.skin === 'dark' ? '#f0f0f0' : '#333333',
          },
        },
        tooltip: {
          formatter() {
            return `<b>${this.y}</b>`
          },
        },
        //     tooltip: {
        //   // pointFormat: this.box.boxConfig.showBase ? '{point.y}<br>n: {this.formatBase(point.base)}' : '{point.y}',
        //   pointFormat: this.box.boxConfig.showBase ? '{point.y}' : '{point.y}',
        // },
        plotOptions: {
          series: {
            point: {
              events: {
                click: e => {
                  this.pointClick(e.point.rowidx, e.point.colidx)
                },
              },
            },
            stacking: this.box.boxConfig.stacking,
            cursor: 'pointer',
            shadow: false,
            lineWidth: 5,
            marker: {
              radius: 6, symbol: 'circle', fillColor: '#FFFFFF', lineWidth: 2, lineColor: null,
            },
            dataLabels: {
              enabled: true,
              allowOverlap: false,
              style: {
                color: this.$store.state.appConfig.layout.skin === 'dark' ? '#f0f0f0' : '#333333',
                fontFamily: "'Montserrat', 'Helvetica Neue', 'Helvetica', 'Arial', 'sans-serif'",
                fontSize: '12px',
                fontWeight: 500,
                textOutline: '',
              },
              y: -20,
            },
          },
          pie: {
            allowPointSelect: true,
            showInLegend: true,
          },
          bar: {
            dataLabels: {
              verticalAlign: 'top',
              y: 5,
            },
          },
          column: {
            dataLabels: {
              verticalAlign: 'top',
              y: this.box.boxConfig.stacking ? 5 : -10,
            },
          },
        },
        xAxis: {
          categories: this.categories,
        },
        yAxis: {
          title: { text: '' },
          min: this.box.boxConfig.axisMin ? this.box.boxConfig.axisMin : null,
          max: this.box.boxConfig.axisMax ? this.box.boxConfig.axisMax : null,
          plotLines: this.box.boxConfig.targetLine ? [{
            color: this.box.boxConfig.targetLineColor ? this.box.boxConfig.targetLineColor : '#FF0000',
            width: 2,
            value: this.box.boxConfig.targetLine,
            label: {
              text: this.box.boxConfig.targetLine,
              align: 'left',
              style: {
                color: this.box.boxConfig.targetLineColor ? this.box.boxConfig.targetLineColor : '#FF0000',
                fontSize: '12px',
              },
            },
          }] : null,
        },
        colors: this.box.boxConfig.colors.length ? this.box.boxConfig.colors : ['#008FFB', '#00E396', '#FEB019', '#FF4560', '#775DD0', '#3F51B5', '#03A9F4', '#4CAF50', '#F9CE1D', '#FF9800', '#33B2DF', '#546E7A', '#D4526E', '#13D8AA', '#A5978B', '#4ECDC4', '#C7F464', '#81D4FA', '#546E7A', '#FD6A6A', '#2B908F', '#F9A3A4', '#90EE7E', '#FA4443', '#69D2E7', '#449DD1', '#F86624', '#EA3546', '#662E9B', '#C5D86D', '#D7263D', '#1B998B', '#2E294E', '#F46036', '#E2C044', '#662E9B', '#F86624', '#F9C80E', '#EA3546', '#43BCCD', '#5C4742', '#A5978B', '#8D5B4C', '#5A2A27', '#C4BBAF', '#A300D6', '#7D02EB', '#5653FE', '#2983FF', '#00B1F2'],
        series: this.series,
      }
    },
    ig_group() {
      if (this.items && this.box.boxConfig.type === 'infographicGroup') {
        const arr = []
        let index = -1
        this.box.boxConfig.infographics.forEach(x => {
          index += 1
          arr.push({
            icon: x.statisticIcon,
            color: x.statisticVariant,
            title: (this.items[index] && this.items[index].value ? this.items[index].value.val : 0),
            text: x.statisticText,
            groupName: x.groupName,
            questionName: x.questionName,
            subtitle: x.showBase ? `n: ${this.items[index] ? this.formatBase(this.items[index].base.val) : 0}` : '',
            size: x.size,
            customClass: '',
          })
        })
        return arr
      }
      return null
    },
    cssVars() {
      return {
        // '--box-height': `${this.box.boxHeight - (this.box.description || (this.result && (this.box.drilledBy || this.result.systemMessage || this.box.boxConfig.type === 'data' || this.box.boxConfig.type === 'external')) ? 90 : 20)}px`,
        '--box-height': `${this.box.boxHeight}px`,
      }
    },
  },
  created() {
    this.initBox('created')
    window.addEventListener('resize', this.myEventHandler)
    this.myEventHandler(window)
  },
  destroyed() {
    window.removeEventListener('resize', this.myEventHandler)
  },
  mounted() {
    this.$root.$on('filterApplied', () => {
      this.initBox('mounted')
    })
    this.$root.$on(`boxUpdated-${this.box.id}`, () => {
      useJwt.axiosIns.get(`box/detail/${this.box.id}`)
        .then(response => {
          if (response.data) {
            this.box = response.data
            this.initBox('boxUpdated')
          }
        })
        .catch(e => {
          this.inProgress = false
          this.$emit('done')
          console.log(e)
        })
    })
    this.$root.$on(`boxSearch-${this.box.id}`, searchObj => {
      this.searchValue = searchObj.searchValue
      this.searchQuestions = searchObj.searchQuestions
      this.initBox()
    })
    this.$root.$on(`boxDrillAdded-${this.box.id}`, () => {
      this.initBox('boxDrillAdded')
    })
    this.$root.$on(`boxDrillDeleted-${this.box.id}`, () => {
      this.drillBack()
    })
    this.$root.$on(`boxDrilled-${this.box.id}`, newId => {
      useJwt.axiosIns.get(`box/detail/${newId}`)
        .then(response => {
          if (response.data) {
            this.box = response.data
            this.initBox('boxDrilled')
          }
        })
        .catch(e => {
          this.inProgress = false
          this.$emit('done')
          console.log(e)
        })
    })
  },
  beforeDestroy() {
    this.$root.$off(`boxUpdated-${this.box.id}`)
    this.$root.$off(`boxSearch-${this.box.id}`)
    this.$root.$off(`boxDrilled-${this.box.id}`)
    this.$root.$off(`boxDrillAdded-${this.box.id}`)
    this.$root.$off(`boxDrillDeleted-${this.box.id}`)
    this.$root.$off('filterApplied')
  },
  methods: {
    async initBox(msg, forExport = false) {
      const userData = getUserData()
      this.authorized = false

      // check box project vs user projects
      if (['superadmin', 'admin'].includes(userData.role)) {
        this.authorized = true
      } else if (userData.userProjects) {
        this.authorized = userData.userProjects.map(p => p.projectId).includes(this.box.projectId)
      }

      // check box groups vs user groups
      if (this.authorized) {
        if (this.box.userGroups && this.box.userGroups.length > 0) {
          this.box.userGroups.forEach(x => {
            if (userData.userGroups.findIndex(y => y.id === x) > -1) {
              this.authorized = true
            }
          })
        } else {
          this.authorized = true
        }
      }

      if (this.authorized) {
        await this.getDrills(msg)
        this.boxtime = new Date()
        const boxRequest = JSON.parse(JSON.stringify(this.$store.state.globals.globalFilter))
        if (this.searchValue) {
          boxRequest.searchValue = this.searchValue
          if (this.searchQuestions.length) {
            boxRequest.searchQuestions = this.searchQuestions.map(q => q.questionName)
          }
        }
        this.box.boxConfig = JSON.parse(this.box.configJson)

        boxRequest.boxConfig = JSON.parse(this.box.configJson)
        boxRequest.boxConfig.drillFilters = this.drillFilters

        if (!boxRequest.boxConfig.includeListData) {
          boxRequest.boxConfig.includeListData = false
        }
        if (boxRequest.boxConfig.showMean === null) {
          boxRequest.boxConfig.showMean = false
        }
        boxRequest.BoxTitle = this.box.boxTitle
        boxRequest.ProjectName = this.box.projectName

        const startTime = dayjs()
        this.duration = 'Running...'

        if (!forExport) {
          this.inProgress = true
          this.fields = []
          this.items = []
        }

        if (this.box.boxConfig.type === 'external') {
          this.fields = this.box.boxConfig.externalData.fields
          for (let i = 0; i < this.fields.length; i += 1) {
            this.fields[i] = { key: this.fields[i], sortable: true }
          }

          this.items = this.box.boxConfig.externalData.items
          this.inProgress = false
          this.duration = '0 second'
        } else {
          if (this.box.boxConfig.type !== 'data') {
            this.perPage = 0
          } else {
            this.perPage = 50
          }
          this.$emit('running')
          useJwt.axiosIns.post(`box/runbox/${this.box.projectId}/${this.box.id}${this.runningForPdf ? '/pdf' : ''}${forExport ? `/export/${this.$i18n.locale}` : ''}`, boxRequest, forExport ? { responseType: 'arraybuffer' } : null)
            .then(async response => {
              if (response.data) {
                if (forExport) {
                  this.openFile(response, `${this.box.projectName}_${this.box.boxTitle}_${dayjs().format('YYYYMMDD')}.xlsx`)
                } else {
                  this.result = response.data

                  this.compareMessage = null
                  if (this.result && this.result.systemMessage && this.result.systemMessage.indexOf('comparing:') === 0) {
                    const arrMessages = this.result.systemMessage.split('~')
                    const comparingMessages = arrMessages[0].split(':')
                    this.compareMessage = `${this.$t(dayjs(comparingMessages[1], 'YYYYMM').format('MMM'))}'${dayjs(comparingMessages[1], 'YYYYMM').format('YY')} - ${this.$t(dayjs(comparingMessages[2], 'YYYYMM').format('MMM'))}'${dayjs(comparingMessages[2], 'YYYYMM').format('YY')}`
                    this.result.systemMessage = arrMessages.splice(1).join('')
                  }

                  if (this.box.boxConfig.orderByScore && this.box.boxConfig.topNItem && this.box.boxConfig.topNItem > 0) {
                    let topN = Number(this.box.boxConfig.topNItem)
                    if (this.box.boxConfig.type === 'frequency') {
                      topN += 1
                    }
                    this.result.rows = this.result.rows.slice(0, topN)
                  }

                  if (this.result.headerRow) {
                    this.fields = []
                    this.result.headerRow.forEach(x => {
                      this.fields.push({
                        key: x.value,
                        label: (x.label ? ` ${this.formatLabel(x.label)}` : ''),
                      })
                    })

                    for (let i = 0; i < this.fields.length; i += 1) {
                      this.fields[i] = {
                        key: this.fields[i].key,
                        testLabel: this.fields[i].label,
                        sortable: true,
                      }
                    }

                    this.items = []
                    this.result.rows.forEach(element => {
                      const newRow = {}
                      this.fields.forEach(h => {
                        if (element[this.fields.indexOf(h)]) {
                          newRow[h.key] = {
                            val: this.translateSome(element[this.fields.indexOf(h)].value),
                            target: element[this.fields.indexOf(h)].targetStatus || element[this.fields.indexOf(h)].trendStatus,
                            lbl: element[this.fields.indexOf(h)].labels && element[this.fields.indexOf(h)].labels.length > 0 ? `${element[this.fields.indexOf(h)].labels.map(label => this.formatLabel(label)).join('')}` : '',
                          }
                        }
                      })
                      this.items.push(newRow)
                    })
                    // set sticky columns
                    if ((this.box.boxConfig.groupNames && this.box.boxConfig.groupNames.length > 0) || this.box.boxConfig.type !== 'data') {
                      const stickyFields = this.fields.slice(0, this.box.boxConfig.questionNames.length + (this.box.boxConfig.questionName ? 1 : 0) + this.box.boxConfig.infoQuestionNames.length + (this.box.boxConfig.type === 'data' ? 1 : 0))
                      for (let i = 0; i < stickyFields.length; i += 1) {
                        this.fields[i].stickyColumn = true
                      }
                      if (this.box.boxConfig.type === 'frequency') {
                        if (this.box.boxConfig.breakByQuestionName && this.box.boxConfig.showBaseColumn) {
                          // add total column
                          this.fields.splice((this.box.boxConfig.questionName ? 1 : 0) + this.box.boxConfig.infoQuestionNames.length, 0, { key: 'total', sortable: true, stickyColumn: true })
                          const nonStickyFields = this.fields.filter(f => !f.stickyColumn).map(f => f.key)
                          this.items.forEach(row => {
                            const rownew = row
                            rownew.total = { val: 0 }
                            nonStickyFields.forEach(f => {
                              if (rownew[f].val) {
                                rownew.total.val += parseFloat(rownew[f].val)
                              }
                            })
                          })
                          // calculate percentage by row total column
                          if (this.box.boxConfig.percentage === 'by row') {
                            this.items.forEach(row => {
                              const rownew = row
                              nonStickyFields.forEach(f => {
                                if (rownew[f].val && rownew.total.val) {
                                  rownew[f].zbase = rownew[f].val
                                  rownew[f].val = this.roundTo((parseFloat(rownew[f].val) / parseFloat(rownew.total.val)) * 100, this.box.boxConfig.decimalPlaces)
                                }
                              })
                            })
                          }
                        }

                        // calculate percentage by base
                        if (this.box.boxConfig.percentage === 'by base') {
                          const nonStickyFields = this.fields.filter(f => !f.stickyColumn).map(f => f.key)
                          let rowIndex = -1
                          let baseRow = null
                          const startRowIndex = this.box.boxConfig.showUnweightedBase ? 1 : 0

                          this.items.forEach(row => {
                            rowIndex += 1
                            if (rowIndex === 0) {
                              baseRow = row
                            } else if (rowIndex > startRowIndex) {
                              const rownew = row
                              if (!['sysmean', 'sysstddev'].includes(row[this.fields[0].key].val)) {
                                nonStickyFields.forEach(f => {
                                  if (rownew[f].val && (rowIndex < this.items.length - 1 || !this.box.boxConfig.showTotalResponseRow)) {
                                    rownew[f].zbase = rownew[f].val
                                    rownew[f].val = this.roundTo((parseFloat(rownew[f].val) / parseFloat(baseRow[f].val)) * 100, this.box.boxConfig.decimalPlaces)
                                  }
                                })
                              }
                            }
                          })

                          if (startRowIndex === 1) {
                            // remove weighted base row.git add .
                            this.items.shift()
                            // rename sysbaseunwgt row label as sysbase
                            Object.keys(this.items[0]).forEach(f => {
                              this.items[0][f].val = this.items[0][f].val.replace('sysbaseunwgt', 'sysbase')
                            })
                          }
                        }

                        // calculate percentage by total response row
                        if (this.box.boxConfig.percentage === 'by total response') {
                          const nonStickyFields = this.fields.filter(f => !f.stickyColumn).map(f => f.key)
                          let rowIndex = -1
                          const totalResponseRow = this.items[this.items.length - 1]
                          this.items.forEach(row => {
                            rowIndex += 1
                            if (rowIndex > 0 && rowIndex < this.items.length - 1) {
                              const rownew = row
                              nonStickyFields.forEach(f => {
                                if (rownew[f].val) {
                                  rownew[f].zbase = rownew[f].val
                                  rownew[f].val = this.roundTo((parseFloat(rownew[f].val) / parseFloat(totalResponseRow[f].val)) * 100, this.box.boxConfig.decimalPlaces)
                                }
                              })
                            }
                          })
                        }
                      }
                    }

                    this.initChartData()

                    await this.getRegionMapping()

                    this.initMapData()

                    // check for mobile...
                    this.myEventHandler()
                  }
                }
              }

              if (this.box.boxConfig.type === 'custom' && this.box.boxConfig.display !== 'table') {
                this.convertToHighchartsData(this.result)
              }

              this.inProgress = false
              const endTime = dayjs()
              const theDuration = endTime.diff(startTime, 'seconds')
              if (theDuration === 0) {
                this.duration = '< 1 second'
              } else if (theDuration === 1) {
                this.duration = '1 second'
              } else {
                this.duration = `${theDuration} seconds`
              }
              this.$emit('done')
            })
            .catch(e => {
              this.inProgress = false
              this.$emit('done')
              console.log(e)
            })
        }
      } else {
        this.duration = '0 second'
        this.inProgress = false
      }
    },
    myEventHandler() {
      // your code for handling resize...
      if (window.innerWidth < 1024) {
        const elements = document.getElementsByClassName('b-table-sticky-column')
        elements.forEach(e => {
          e.classList.add('b-table-sticky-column-OLD')
          e.classList.remove('b-table-sticky-column')
        })
      } else {
        const elements = document.getElementsByClassName('b-table-sticky-column-OLD')
        elements.forEach(e => {
          e.classList.add('b-table-sticky-column')
          e.classList.remove('b-table-sticky-column-OLD')
        })
      }
    },
    pageChanged(newPageIndex) {
      this.currentPage = newPageIndex
    },
    clearSearch() {
      this.searchValue = null
      this.searchQuestions = []
      this.initBox()
    },
    mysorter(aRow, bRow, key, sortDesc, formatter, compareOptions, compareLocale) {
      const idx = this.items.findIndex(i => i === bRow)

      let result = null
      const a = aRow[key].val // or use Lodash `_.get()`
      const b = bRow[key].val

      // SKIP IF FIRST and GENERAL ROW
      if (idx === 0 && ((this.box.boxConfig.breakByQuestionName && (this.box.boxConfig.showBase || this.box.boxConfig.sigTest)) || ((this.box.boxConfig.type === 'frequency' || this.box.boxConfig.type === 'standard') && this.box.boxConfig.showBase) || this.box.boxConfig.showGeneral)) { return 0 }

      if (
        (typeof a === 'number' && typeof b === 'number')
        || (a instanceof Date && b instanceof Date)
      ) {
        // If both compared fields are native numbers or both are native dates
        // eslint-disable-next-line no-nested-ternary
        result = a < b ? -1 : a > b ? 1 : 0
      }
      // Otherwise stringify the field data and use String.prototype.localeCompare
      result = this.toString(a).localeCompare(this.toString(b), compareLocale, compareOptions)
      return result
    },
    toString(value) {
      if (value === null || typeof value === 'undefined') {
        return ''
      } if (value instanceof Object) {
        return Object.keys(value)
          .sort()
          .map(key => toString(value[key]))
          .join(' ')
      }
      return String(value)
    },
    searchQuestion(qkey) {
      if (this.result) {
        const question = this.result.questions.find(q => q.questionName === qkey)
        if (question) {
          return question.questionText
        }
      }

      return qkey
    },
    async initMapData() {
      this.map_data = null
      if (this.result.rows && this.box.boxConfig.type === 'infographic' && this.box.boxConfig.layout === 'map-tr') {
        const isGrouped = this.box.boxConfig.mapGroupBy
          && this.result.rows.some(x => x[3] && x[3].value)

        if (isGrouped) {
          // When grouping is enabled, the backend returns one row per city with
          // the region's aggregated value in the "region" column (x[3]).
          // Only rows that carry a value belong to the active grouping; the rest
          // (empty value / base 0) are ignored.
          const regions = new Map()
          this.result.rows.forEach(x => {
            const city = x[2] && x[2].value
            const region = x[3] && x[3].value
            const value = x[0] && x[0].value
            if (!region || !city || value === null || value === undefined || value === '') {
              return
            }
            if (!regions.has(region)) {
              let base = null
              if (this.box.boxConfig.showBase && x[1]) {
                base = x[1].value
              }
              regions.set(region, {
                value,
                base,
                cities: [],
              })
            }
            regions.get(region).cities.push(city)
          })

          const arr = []
          const mapping = []
          let idx = -1
          regions.forEach((r, region) => {
            idx += 1
            arr.push({
              name: region,
              idx,
              value: r.value,
              base: r.base,
            })
            r.cities.forEach(city => mapping.push({ city, region }))
          })
          this.box.boxConfig.regionMapping = mapping
          this.map_data = arr
        } else {
          const arr = []
          let idx = -1
          this.result.rows.forEach(x => {
            idx += 1
            if (x[2].value) {
              arr.push({
                name: x[2].value,
                idx,
                value: x[0].value,
                base: this.box.boxConfig.showBase ? x[1].value : null,
              })
            }
          })
          this.box.boxConfig.regionMapping = []
          this.map_data = arr
        }
      }
    },
    initChartData() {
      if (this.box.boxConfig.display !== 'table' && (this.box.boxConfig.type === 'score' || this.box.boxConfig.type === 'standard' || this.box.boxConfig.type === 'frequency' || this.box.boxConfig.type === 'custom')) {
        const series = []
        const valueCols = this.fields.filter(f => (f.key !== 'base' || this.box.boxConfig.type === 'frequency') && !f.stickyColumn && f.key !== 'groupname')
        if (this.items && this.items.length > 0) {
          if (this.box.boxConfig.display === 'pie' || this.box.boxConfig.display === 'treemap') {
            const serie = {}
            serie.name = this.fields[0].key
            serie.data = []

            const totalBase = this.items.find(d => d[this.fields[0].key].val === 'sysbase').base.val

            this.items.filter(d => d[this.fields[0].key].val !== 'sysbase').forEach(x => {
              valueCols.forEach(v => {
                if (x[v.key].val) {
                  // eslint-disable-next-line no-nested-ternary
                  // totalBase += parseFloat(x.base.zbase ? x.base.zbase : (x.base ? x.base.val : 0))

                  serie.data.push({
                    name: x[this.fields[0].key].val,
                    y: parseFloat(x[v.key].val),
                    rowidx: this.items.findIndex(i => i[this.fields[0].key].val === x[this.fields[0].key].val),
                    colidx: this.fields.findIndex(f => f.key === v.key),
                    // eslint-disable-next-line no-nested-ternary
                    base: x.base.zbase ? x.base.zbase : (x.base ? x.base.val : ''),
                    dataLabels: {
                      enabled: true,
                      allowOverlap: false,
                      // eslint-disable-next-line no-nested-ternary
                      format: `<span style="font-size: 1.0em">${this.box.boxConfig.percentage !== 'no' && this.box.boxConfig.type === 'frequency' ? '%' : ''} ${this.roundTo(parseFloat(x[v.key].val), this.box.boxConfig.decimalPlaces)}${this.box.boxConfig.showBase && this.box.boxConfig.showOptionsBase ? `<br><span style="font-size: 0.8em">n: ${x.base.zbase ? this.formatBase(x.base.zbase) : (x.base ? this.formatBase(x.base.val) : '')}` : ''}</span></span>`,
                      verticalAlign: 'middle',
                    },
                  })
                }
              })
            })
            series.push(serie)

            this.totalBaseForPie = null
            if (this.box.boxConfig.showBase || (this.box.boxConfig.percentage === 'no' && this.box.boxConfig.type === 'frequency')) {
              this.totalBaseForPie = `n: ${this.formatBase(totalBase)}`
            }
          } else {
            this.items.filter(d => d[this.fields[0].key].val !== 'sysbase').forEach(x => {
              const serie = {}

              // eslint-disable-next-line no-nested-ternary
              const baseVal = (x.base && x.base.zbase !== null ? x.base.zbase : (x.base && x.base.val ? x.base.val : 0))
              if (((this.box.boxConfig.percentage !== 'no' && this.box.boxConfig.type === 'frequency') || this.box.boxConfig.type !== 'frequency') && baseVal) {
                serie.name = x[this.fields[0].key].val + (this.box.boxConfig.showBase && this.box.boxConfig.showOptionsBase ? ` (n: ${this.formatBase(baseVal)})` : '')
              } else {
                serie.name = x[this.fields[0].key].val
              }
              if (serie.name === ' ') {
                serie.name = this.$t('TOTAL')
              }

              serie.data = []
              valueCols.forEach(v => {
                if (x[v.key].val) {
                  serie.data.push({
                    y: parseFloat(x[v.key].val),
                    base: x.base ? this.formatBase(x.base.val) : null,
                    rowidx: this.items.findIndex(i => i[this.fields[0].key].val === x[this.fields[0].key].val),
                    colidx: this.fields.findIndex(f => f.key === v.key),
                    dataLabels: {
                      enabled: true,
                      format: `<span style="font-size: 1.3em">${this.box.boxConfig.percentage !== 'no' ? '%' : ''} ${this.roundTo(parseFloat(x[v.key].val), this.box.boxConfig.decimalPlaces)} ${x[v.key].lbl}</span>`,
                      verticalAlign: 'middle',
                    },
                  })
                } else {
                  serie.data.push({
                    y: null,
                    base: null,
                    dataLabels: {
                      enabled: false,
                    },
                  })
                }
              })
              if (serie.data && serie.data.length && serie.data[0].base && this.box.boxConfig.showBaseOnChartItems) {
                serie.name += ` </br>n: ${this.formatBase(serie.data[0].base)}`
              }
              serie.name = serie.name.replace('sysmean', `${this.$t('sysmean')}`).replace('sysstddev', `${this.$t('sysstddev')}`).replace('sysbase', `${this.$t('sysbase')}`)
              series.push(serie)
            })
          }
        }
        this.series = series.slice(0, this.maxSeriesLimit)
        this.categories = valueCols.filter(x => x.key !== 'groupname').map(x => x.key)
        console.log('this.categories', this.categories, this.result.headerRow)

        if (this.box.boxConfig?.breakByQuestionName && this.items && this.items[0]) {
          this.categories = this.categories.map(c => `${this.$t(c)}${this.findSignLabel(c)}${this.box.boxConfig.showBase && this.items[0][c] ? `<br>n: ${this.formatBase(this.items[0][c].val)}` : ''}`)
        } else if (this.box.boxConfig?.showGeneral && this.items && this.items[0] && this.items[0].base) {
          this.categories = this.categories.map(c => `${this.$t(c)}${this.findSignLabel(c)}${this.box.boxConfig.showBase ? `<br>n: ${this.formatBase(this.items[0].base.val)}` : ''}`)
        } else if (this.items && this.items[0] && this.items[0].base && !this.items[0].base.zbase) {
          this.categories = this.categories.map(c => `${this.$t(c)}${this.findSignLabel(c)}${this.box.boxConfig.showBase ? `<br>n: ${this.formatBase(this.items[0].base.val)}` : ''}`)
        } else {
          let totalBase = 0
          this.items.forEach(x => {
            if (x.base && x.base.zbase) {
              totalBase += parseFloat(x.base.zbase)
            }
          })

          const sysbase = this.items.find(d => d[this.fields[0].key].val === 'sysbase')

          this.categories = this.categories.map(c => `${this.$t(c)}${this.findSignLabel(c)}${this.box.boxConfig.showBase ? `<br>n: ${sysbase && sysbase[c] ? this.formatBase(sysbase[c].val) : this.formatBase(totalBase)}` : ''}`)
        }
        if (this.box.boxConfig.transposeChart) {
          const categoriesTransposed = this.series.map(s => s.name)
          const seriesTransposed = []
          let idx = -1
          this.categories.forEach(c => {
            idx += 1
            const data = []
            this.series.forEach(s => {
              data.push(s.data[idx])
            })
            seriesTransposed.push({ name: c.replace('base<br>', '').replace('Baz<br>', ''), data })
          })

          this.series = seriesTransposed.slice(0, this.maxSeriesLimit)
          this.categories = categoriesTransposed
        }

        // total and ytd colors
        if (this.box.boxConfig.colorYTD || this.box.boxConfig.colorTotal) {
          this.series.forEach((s, index) => {
            if (this.box.boxConfig.colorYTD && s.name.indexOf('YTD') > -1) {
              this.series[index].color = this.box.boxConfig.colorYTD
            }
            if (this.box.boxConfig.colorTotal && (s.name.indexOf('Toplam') > -1 || s.name.indexOf('Total') > -1)) {
              this.series[index].color = this.box.boxConfig.colorTotal
            }
          })
        }

        // remove base/Baz
        this.categories = this.categories.map(c => c.replace('base<br>', '').replace('Baz<br>', ''))

        // if this.box.boxConfig.comparePeriods && this.box.boxConfig.redGreen
        if (this.box.boxConfig.comparePeriods && this.box.boxConfig.redGreen) {
          this.series.forEach((s, sindex) => {
            // make positive values green, negative values red color
            s.data.forEach((d, index) => {
              if (d.y !== null && d.y !== 0) {
                if (d.y > 0) {
                  this.series[sindex].data[index].color = '#28a745'
                } else {
                  this.series[sindex].data[index].color = '#dc3545'
                }
              }
            })
          })
        }
      }
    },
    findSignLabel(c) {
      if (this.box.boxConfig.sigTest) {
        return this.result.headerRow.find(h => h.value === c && h.label) ? ` (${this.formatLabel(this.result.headerRow.find(h => h.value === c && h.label).label)})` : ''
      }
      return ''
    },
    cellClick(data) {
      if (data.field.key === 'orjref') {
        if (this.$refs[`surveyModal${this.box.id}`]) {
          this.$refs[`surveyModal${this.box.id}`].loadSurveyData(this.box.projectId, data.item.orjref.val)
        } else if (this.$refs[`surveyModal${this.box.drilledBy}`]) {
          this.$refs[`surveyModal${this.box.drilledBy}`].loadSurveyData(this.box.projectId, data.item.orjref.val)
        }
      } else {
        this.clickHandler(data.item, data.field)
      }
    },
    pointClick(rowIdx, colIdx) {
      this.clickHandler(this.items[rowIdx], this.fields[colIdx])
    },
    mapClick(name) {
      const item = this.items.find(row => row.cities.val === name)
      const field = this.fields.find(f => f.key === 'cities')
      this.clickHandler(item, field, { questionName: 'il', value: name })
    },
    async clickHandler(item, field, forcedFilters = null) {
      // let groupName = null
      const filters = []
      if (this.box.boxConfig.questionNames.length) {
        this.box.boxConfig.questionNames.forEach(q => {
          if (item[q].val !== ' ' && item[q].val !== 'sysbase' && item[q].val !== 'syscumulative' && item[q].val !== 'total') {
            filters.push({ questionName: q, value: item[q].val })
          }
        })
      } else if (this.box.boxConfig.questionName) {
        if (item[this.box.boxConfig.questionName].val !== ' ' && item[this.box.boxConfig.questionName].val !== 'sysbase' && item[this.box.boxConfig.questionName].val !== 'syscumulative' && item[this.box.boxConfig.questionName].val !== 'total') {
          filters.push({ questionName: this.box.boxConfig.questionName, value: item[this.box.boxConfig.questionName].val })
        }
      }
      if (this.box.boxConfig.breakByQuestionName && field.key !== 'base' && field.key !== 'syscumulative' && field.key !== 'total' && filters.findIndex(f => f.questionName === field.key) === -1) {
        if (!this.box.boxConfig.questionName || this.box.boxConfig.questionName !== field.key) {
          filters.push({ questionName: this.box.boxConfig.breakByQuestionName, value: field.key })
        }
      }
      // if (this.box.boxConfig.groupNames.length && this.box.boxConfig.groupNames.findIndex(g => g === field.key) > -1) {
      //   groupName = field.key
      // }
      if (forcedFilters) {
        filters.push(forcedFilters)
      }
      if (this.drills.length > 1) {
        if (this.currentBoxIndex === 0 || (this.currentBoxIndex > 0 && this.currentBoxIndex < this.drills.length - 1)) {
          this.drillFilters.push(filters)
          this.$emit('drilling', this.drills[this.currentBoxIndex + 1], this.drills[0].id)
        }
      }
    },
    async drillBack() {
      const currentBoxIdx = this.drills.findIndex(d => d.id === this.box.id)
      if (this.drills.length > 1) {
        if (currentBoxIdx > 0) {
          if (this.drillFilters && this.drillFilters.length) {
            this.drillFilters.pop()
          }
          this.$emit('drilling', this.drills[currentBoxIdx - 1], this.drills[0].id)
        }
      }
    },
    formatLabel(label) {
      let fLabel = label
      if (fLabel === '[up]') {
        fLabel = `<span class="sig up ${this.box.boxConfig.noColors ? 'nocolor' : ''}">&#x279a;</span>`
      } else if (fLabel === '[down]') {
        fLabel = `<span class="sig down ${this.box.boxConfig.noColors ? 'nocolor' : ''}">&#x2798;</span>`
      } else {
        fLabel = `<span class="sig label">${fLabel}</span>`
      }
      return fLabel
    },
    formatValues(value) {
      const fValue = value.split('|').filter(x => x.length > 0).join(' / ')
      return fValue
    },
    translateSome(val) {
      if (val === 'systop') {
        return `${this.$t(val)} ${this.box.boxConfig.top}`
      }
      if (val === 'sysbottom') {
        return `${this.$t(val)} ${this.box.boxConfig.bottom}`
      }
      return val
    },
    download() {
      this.initBox('exporting', true)
    },
    openFile(response, filename) {
      const type = response.headers['content-type']
      const blob = new Blob([response.data], {
        type,
        encoding: 'UTF-8',
      })
      const link = document.createElement('a')
      link.href = window.URL.createObjectURL(blob)
      link.download = filename
      link.click()
      link.remove()
    },
    async getDrills() {
      if (this.box.id) {
        await useJwt.axiosIns.get(`box/drills/${this.box.id}`)
          .then(response => {
            if (response.data) {
              this.drills = response.data
              this.currentBoxIndex = this.drills.findIndex(d => d.id === this.box.id)
            }
          })
          .catch(e => {
            console.log(e)
          })
      }
    },
    async getRegionMapping() {
      if (this.box.boxConfig.mapGroupBy) {
        await useJwt.axiosIns.get(`box/RegionList/${this.box.projectId}/${this.box.boxConfig.mapGroupBy}`)
          .then(response => {
            if (response.data) {
              this.box.boxConfig.regionMapping = response.data
            }
          })
          .catch(e => {
            console.log(e)
          })
      } else {
        this.box.boxConfig.regionMapping = []
      }
    },
    formatBase(val) {
      if (val !== null) {
        return (Number(val)).toFixed(0)
      }
      return val
    },
    convertToHighchartsData(rawData) {
      const { headerRow, rows } = rawData

      // Extract x-axis categories (skip first two headers: "proje" and "Baz")
      const categories = headerRow.slice(2).map(col => col.value)

      // Extract series
      const series = rows.map(row => {
        const name = `${row[0].value} (n: ${row[1].value})` // Combine first two columns for name

        const data = row.slice(2).map(cell => {
          const val = cell.value
          // Convert to number or null
          return val === '' || val === null ? null : parseFloat(val)
        })

        return {
          name,
          data,
        }
      })
      this.categories = categories
      this.series = series
    },
  },
}

</script>
<style lang="scss">
.mycard {
  display: inline-block;
}

.sig {
  display: inline-block;
  padding: 2px;
  margin: 2px;
}

.up {
  color: green !important;
}

.down {
  color: red !important;
}

.nocolor {
  color: #000000 !important;
}

.above {
  padding: 3px;
  background-color: green !important;
  color: white !important;
}
.below {
  padding: 3px;
  background-color: red !important;
  color: white !important;
}

.label {
  background-color: #dddddd;
  color: #333333 !important;
}

.link {
  color: blue !important;
  cursor: pointer;
}

.b-table-sticky-header {
  max-height: var(--box-height)
}
</style>
